import React, { useState, useEffect } from "react";

// Chakra imports
import {
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Text,
  useColorModeValue,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";

// Custom components
import Banner from "views/admin/marketplace/components/Banner";
import TableTopCreators from "views/admin/marketplace/components/TableTopCreators";
import HistoryItem from "views/admin/marketplace/components/HistoryItem";
import NFT from "components/card/NFT";
import Card from "components/card/Card.js";

// Services
import { getBlogs } from "services/blogService";
import config from "config/index";

// Assets
import Avatar1 from "assets/img/avatars/avatar1.png";
import Avatar2 from "assets/img/avatars/avatar2.png";
import Avatar3 from "assets/img/avatars/avatar3.png";
import Avatar4 from "assets/img/avatars/avatar4.png";
import tableDataTopCreators from "views/admin/marketplace/variables/tableDataTopCreators.json";
import { tableColumnsTopCreators } from "views/admin/marketplace/variables/tableColumnsTopCreators";

export default function Marketplace() {
  // Chakra Color Mode
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const toast = useToast();

  // State
  const [publishedBlogs, setPublishedBlogs] = useState([]);
  const [draftBlogs, setDraftBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setCategories] = useState([]);

  // Fetch blogs function
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      // Fetch published blogs
      const publishedResult = await getBlogs({
        skip: 0,
        limit: 100,
        status_filter: 'PUBLISHED',
      });

      // Fetch draft blogs
      const draftResult = await getBlogs({
        skip: 0,
        limit: 100,
        status_filter: 'DRAFT',
      });

      if (publishedResult.success && publishedResult.data) {
        setPublishedBlogs(publishedResult.data);
      }

      if (draftResult.success && draftResult.data) {
        setDraftBlogs(draftResult.data);
      }

      // Extract unique categories
      const allBlogs = [
        ...(publishedResult.data || []),
        ...(draftResult.data || []),
      ];
      const uniqueCategories = [
        ...new Set(allBlogs.map((blog) => blog.category).filter(Boolean)),
      ];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load blogs. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch blogs on component mount
  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle blog publish
  const handleBlogPublish = (blogId) => {
    // Refresh the blog list after publishing
    fetchBlogs();
  };

  // Handle blog unpublish
  const handleBlogUnpublish = (blogId) => {
    // Refresh the blog list after unpublishing
    fetchBlogs();
  };

  // Helper function to get image URL from blog
  const getBlogImage = (blog) => {
    if (blog.images && blog.images.length > 0) {
      const imageUrl = blog.images[0];
      // If URL is relative, prepend base URL
      if (imageUrl.startsWith('/')) {
        return `${config.api.baseURL}${imageUrl}`;
      }
      return imageUrl;
    }
    // Default placeholder image
    return Avatar1;
  };

  // Helper function to format author name
  const getAuthorName = (blog) => {
    if (blog.created_by_user) {
      return `By ${blog.created_by_user.name || blog.created_by_user.email || 'Unknown'}`;
    }
    return 'By Unknown Author';
  };

  // Helper function to format likes count
  const getLikesText = (likes) => {
    if (likes >= 1000) {
      return `${(likes / 1000).toFixed(1)}K`;
    }
    return likes?.toString() || '0';
  };

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
      {/* Main Fields */}
      <Grid
        mb='20px'
        gridTemplateColumns={{ xl: "repeat(3, 1fr)", "2xl": "1fr 0.46fr" }}
        gap={{ base: "20px", xl: "20px" }}
        display={{ base: "block", xl: "grid" }}>
        <Flex
          flexDirection='column'
          gridArea={{ xl: "1 / 1 / 2 / 3", "2xl": "1 / 1 / 2 / 2" }}>
          <Banner />
          <Flex direction='column'>
            <Flex
              mt='45px'
              mb='20px'
              justifyContent='space-between'
              direction={{ base: "column", md: "row" }}
              align={{ base: "start", md: "center" }}
              data-section='published'>
              <Text color={textColor} fontSize='2xl' ms='24px' fontWeight='700'>
                Published Blogs
              </Text>
              <Flex
                align='center'
                me='20px'
                ms={{ base: "24px", md: "0px" }}
                mt={{ base: "20px", md: "0px" }}>
                <Link
                  color={textColorBrand}
                  fontWeight='500'
                  me={{ base: "34px", md: "44px" }}
                  to='#art'>
                  Art
                </Link>
                <Link
                  color={textColorBrand}
                  fontWeight='500'
                  me={{ base: "34px", md: "44px" }}
                  to='#music'>
                  Music
                </Link>
                <Link
                  color={textColorBrand}
                  fontWeight='500'
                  me={{ base: "34px", md: "44px" }}
                  to='#collectibles'>
                  Collectibles
                </Link>
                <Link color={textColorBrand} fontWeight='500' to='#sports'>
                  Sports
                </Link>
              </Flex>
            </Flex>
            {loading ? (
              <Center py='40px'>
                <Spinner size='xl' color={textColorBrand} />
              </Center>
            ) : publishedBlogs.length > 0 ? (
              <SimpleGrid columns={{ base: 1, md: 3 }} gap='20px'>
                {publishedBlogs.map((blog) => (
                  <NFT
                    key={blog.id}
                    name={blog.title}
                    author={getAuthorName(blog)}
                    bidders={[Avatar1, Avatar2, Avatar3, Avatar4]}
                    image={getBlogImage(blog)}
                    currentbid={`${getLikesText(blog.likes || 0)} likes`}
                    download={`/admin/blog/${blog.id}`}
                    blogId={blog.id}
                    isLiked={blog.is_liked || false}
                    likes={blog.likes || 0}
                    status={blog.status}
                    onUnpublish={handleBlogUnpublish}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <Box p='40px' textAlign='center'>
                <Text color={textColor} fontSize='lg'>
                  No published blogs found
                </Text>
              </Box>
            )}
            <Text
              mt='45px'
              mb='36px'
              color={textColor}
              fontSize='2xl'
              ms='24px'
              fontWeight='700'>
              Draft Blogs
            </Text>
            {loading ? (
              <Center py='40px'>
                <Spinner size='xl' color={textColorBrand} />
              </Center>
            ) : draftBlogs.length > 0 ? (
              <SimpleGrid
                columns={{ base: 1, md: 3 }}
                gap='20px'
                mb={{ base: "20px", xl: "0px" }}>
                {draftBlogs.map((blog) => (
                  <NFT
                    key={blog.id}
                    name={blog.title}
                    author={getAuthorName(blog)}
                    bidders={[Avatar1, Avatar2, Avatar3, Avatar4]}
                    image={getBlogImage(blog)}
                    currentbid={`${getLikesText(blog.likes || 0)} likes`}
                    download={`/admin/blog/${blog.id}`}
                    blogId={blog.id}
                    isLiked={blog.is_liked || false}
                    likes={blog.likes || 0}
                    status={blog.status}
                    onPublish={handleBlogPublish}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <Box p='40px' textAlign='center'>
                <Text color={textColor} fontSize='lg'>
                  No draft blogs found
                </Text>
              </Box>
            )}
          </Flex>
        </Flex>
        <Flex
          flexDirection='column'
          gridArea={{ xl: "1 / 3 / 2 / 4", "2xl": "1 / 2 / 2 / 3" }}>
          <Card px='0px' mb='20px'>
            <TableTopCreators
              tableData={tableDataTopCreators}
              columnsData={tableColumnsTopCreators}
            />
          </Card>
          <Card p='0px'>
            <Flex
              align={{ sm: "flex-start", lg: "center" }}
              justify='space-between'
              w='100%'
              px='22px'
              py='18px'>
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Top Category
              </Text>
              <Button variant='action'>See all</Button>
            </Flex>

            {/* Show recent published blogs in history */}
            {publishedBlogs.slice(0, 6).map((blog, index) => {
              // Calculate time ago
              const getTimeAgo = (dateString) => {
                if (!dateString) return 'Recently';
                const date = new Date(dateString);
                const now = new Date();
                const diffInSeconds = Math.floor((now - date) / 1000);
                
                if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
                if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
                if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
                return `${Math.floor(diffInSeconds / 86400)}d ago`;
              };

              return (
                <HistoryItem
                  key={blog.id}
                  name={blog.title}
                  author={getAuthorName(blog)}
                  date={getTimeAgo(blog.created_at)}
                  image={getBlogImage(blog)}
                  price={`${getLikesText(blog.likes || 0)} likes`}
                />
              );
            })}
            {publishedBlogs.length === 0 && !loading && (
              <Box p='20px' textAlign='center'>
                <Text color={textColor} fontSize='sm'>
                  No recent blogs
                </Text>
              </Box>
            )}
          </Card>
        </Flex>
      </Grid>
      {/* Delete Product */}
    </Box>
  );
}
